import { api } from './api';

const retryRequest = async (requestFn, maxRetries = 3, delay = 1000) => {
  for (let i = 0; i < maxRetries; i++) {
    try {
      return await requestFn();
    } catch (error) {
      if (i === maxRetries - 1) throw error;
      await new Promise(resolve => setTimeout(resolve, delay * Math.pow(2, i)));
    }
  }
};

class IntegrationsService {
  async verifyAllConnections(authorizationToken, tenant) {
    try {
      const endpoint = `/v1/connections/verify`;
      const headers = {};
      if (authorizationToken) headers['Authorization'] = 'Bearer ' + authorizationToken;
      if (tenant) headers['tenant'] = tenant;

      const response = await retryRequest(async () => {
        return await api.get(endpoint, { headers });
      });
      return response.data;
    } catch (error) {
      console.error('Error verifying connections:', error);
      throw error;
    }
  }

  async areAllConnectionsReady(authorizationToken, tenant) {
    try {
      const status = await this.verifyAllConnections(authorizationToken, tenant);
      return status.summary?.all_valid || false;
    } catch (error) {
      console.error('Error checking if connections are ready:', error);
      return false;
    }
  }

  async getConnectionIssues(authorizationToken, tenant) {
    try {
      const status = await this.verifyAllConnections(authorizationToken, tenant);

      const issues = {
        needsAttention: [],
        details: {}
      };

      if (!status.hubspot?.connected) {
        issues.needsAttention.push('hubspot');
        issues.details.hubspot = 'Not connected';
      } else if (!status.hubspot?.token_valid) {
        issues.needsAttention.push('hubspot');
        issues.details.hubspot = status.hubspot.error || 'Token invalid';
      }

      if (!status.linkedin?.connected) {
        issues.needsAttention.push('linkedin');
        issues.details.linkedin = 'Not connected';
      } else if (!status.linkedin?.token_valid) {
        issues.needsAttention.push('linkedin');
        issues.details.linkedin = status.linkedin.message || 'Token invalid';
      }

      const emailConnected = status.email?.google?.connected || status.email?.microsoft?.connected;
      const emailValid = status.email?.google?.token_valid || status.email?.microsoft?.token_valid;

      if (!emailConnected) {
        issues.needsAttention.push('email');
        issues.details.email = 'No email provider connected';
      } else if (!emailValid) {
        issues.needsAttention.push('email');
        const errors = [];
        if (status.email?.google?.connected && !status.email?.google?.token_valid) {
          errors.push(`Google: ${status.email.google.error || 'Token invalid'}`);
        }
        if (status.email?.microsoft?.connected && !status.email?.microsoft?.token_valid) {
          errors.push(`Microsoft: ${status.email.microsoft.error || 'Token invalid'}`);
        }
        issues.details.email = errors.join(', ');
      }

      return issues;
    } catch (error) {
      console.error('Error getting connection issues:', error);
      throw error;
    }
  }

  async disconnectProvider(provider, authorizationToken, tenant) {
    try {
      const endpoint = `/v1/connections/disconnect/${provider}`;
      const headers = {};
      if (authorizationToken) headers['Authorization'] = 'Bearer ' + authorizationToken;
      if (tenant) headers['tenant'] = tenant;

      const response = await retryRequest(async () => {
        return await api.post(endpoint, {}, { headers });
      });
      return response.data;
    } catch (error) {
      console.error(`Error disconnecting ${provider}:`, error);
      throw error;
    }
  }

  async connectProvider(provider, authorizationToken, tenant) {
    try {
      const endpoint = `/v1/integrations/connect/${provider}`;
      const headers = {};
      if (authorizationToken) headers['Authorization'] = 'Bearer ' + authorizationToken;
      if (tenant) headers['tenant'] = tenant;

      const response = await retryRequest(async () => {
        return await api.post(endpoint, {}, { headers });
      });
      return response.data;
    } catch (error) {
      console.error(`Error connecting ${provider}:`, error);
      throw error;
    }
  }
}

export const integrationsService = new IntegrationsService();
export default integrationsService;
